import React, { useState, useCallback, useRef, useEffect } from "react";
import { authFetch } from "../pages/Auth";
import { RISK_COLORS } from "../chartTheme";

export default function PatientSearch({ onSelectPatient, onComparePatient, comparePatients = [] }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [riskScores, setRiskScores] = useState({});
  const [sortByRisk, setSortByRisk] = useState(false);
  const [highRiskOnly, setHighRiskOnly] = useState(false);
  const debounceRef = useRef(null);

  // Fetch risk scores lazily — only when search results are displayed
  useEffect(() => {
    if (!results?.patients?.length || Object.keys(riskScores).length > 0) return;
    authFetch("/api/ml/dropout-risk")
      .then((r) => r.json())
      .then((data) => {
        if (data?.patients) {
          const map = {};
          data.patients.forEach((p) => {
            map[p.patient_id] = {
              score: p.dropout_risk_score,
              category: p.risk_category,
              factors: p.top_risk_factors,
            };
          });
          setRiskScores(map);
        }
      })
      .catch(() => {}); // ML not trained yet — ok
  }, [results]); // eslint-disable-line react-hooks/exhaustive-deps

  const search = useCallback(async (q, p = 1) => {
    if (!q || q.trim().length < 2) { setResults(null); setError(""); return; }
    setLoading(true); setError("");
    try {
      const res = await authFetch(`/api/patients/search?q=${encodeURIComponent(q.trim())}&page=${p}&limit=20`);
      const data = await res.json();
      if (!res.ok) { setError(data.error || "Search failed"); setResults(null); }
      else { setResults(data); setPage(p); }
    } catch { setError("Unable to connect to server"); setResults(null); }
    setLoading(false);
  }, []);

  const handleInputChange = (e) => {
    const val = e.target.value; setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val, 1), 400);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") { if (debounceRef.current) clearTimeout(debounceRef.current); search(query, 1); }
  };

  const pagination = results?.pagination;

  return (
    <div>
      <div style={s.searchRow}>
        <div style={s.searchIcon}>&#x2315;</div>
        <input value={query} onChange={handleInputChange} onKeyDown={handleKeyDown}
          placeholder="Search patients by name or ID (min 2 characters)..." style={s.input} />
        {loading && <div style={s.spinner} />}
      </div>

      {error && (
        <div style={s.errorBanner}>
          <span style={s.errorIcon}>!</span>{error}
          <button onClick={() => search(query, page)} style={s.retryBtn}>Retry</button>
        </div>
      )}

      {results && !error && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div style={s.resultsMeta}>
              {pagination.total} patient{pagination.total !== 1 ? "s" : ""} found
              {pagination.pages > 1 && ` \u2014 page ${pagination.page} of ${pagination.pages}`}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setHighRiskOnly(!highRiskOnly)}
                style={{...s.filterBtn, background: highRiskOnly ? "rgba(255,77,109,0.15)" : "#131920",
                  color: highRiskOnly ? "#ff4d6d" : "#4a6175",
                  border: highRiskOnly ? "1px solid rgba(255,77,109,0.3)" : "1px solid #1e2d3d"}}>
                {highRiskOnly ? "Showing High Risk" : "High Risk Only"}
              </button>
              <button onClick={() => setSortByRisk(!sortByRisk)}
                style={{...s.filterBtn, background: sortByRisk ? "rgba(0,229,255,0.1)" : "#131920",
                  color: sortByRisk ? "#00e5ff" : "#4a6175",
                  border: sortByRisk ? "1px solid rgba(0,229,255,0.3)" : "1px solid #1e2d3d"}}>
                {sortByRisk ? "Sorted by Risk" : "Sort by Risk"}
              </button>
            </div>
          </div>

          {results.patients.length === 0 ? (
            <div style={s.emptyState}><div style={{fontSize:24,marginBottom:8}}>&empty;</div><div>No patients match "{query}"</div></div>
          ) : (
            <div style={{overflowX:"auto"}}>
              <table style={s.table}>
                <thead><tr>
                  <th style={s.th}>Name</th><th style={s.th}>Patient ID</th><th style={s.th}>DOB</th>
                  <th style={s.th}>Program</th><th style={s.th}>Status</th><th style={s.th}>Risk</th><th style={s.th}>State</th>
                </tr></thead>
                <tbody>
                  {(() => {
                    let patients = [...results.patients];
                    if (highRiskOnly) {
                      patients = patients.filter((p) => riskScores[p.patient_id]?.category === "high");
                    }
                    if (sortByRisk) {
                      patients.sort((a, b) => (riskScores[b.patient_id]?.score ?? -1) - (riskScores[a.patient_id]?.score ?? -1));
                    }
                    return patients.map((p) => {
                      const risk = riskScores[p.patient_id];
                      const riskColor = risk ? (RISK_COLORS[risk.category] || "#4a6175") : "#4a6175";
                      return (
                        <tr key={p.patient_id} style={s.row}
                          onClick={() => onSelectPatient && onSelectPatient(p)}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "#131920")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                          <td style={s.td}><span style={s.name}>{p.given_name?.split(" ")[0]} {p.family_name}</span></td>
                          <td style={{...s.td,...s.mono}}>{p.patient_id?.slice(0,8)}...</td>
                          <td style={s.td}>{fmtDate(p.birth_date)}</td>
                          <td style={s.td}>{p.program_name || "\u2014"}</td>
                          <td style={s.td}>
                            <span style={{...s.badge,background:p.is_active?"rgba(52,211,153,0.15)":"rgba(251,191,36,0.15)",
                              color:p.is_active?"#34d399":"#fbbf24"}}>{p.is_active?"Active":"Inactive"}</span>
                          </td>
                          <td style={s.td}>
                            {risk ? (
                              <span style={{...s.badge, background: `${riskColor}18`, color: riskColor, border: `1px solid ${riskColor}44`}}>
                                {risk.category} &middot; {(risk.score * 100).toFixed(0)}
                              </span>
                            ) : (
                              <span style={{ color: "#4a6175", fontSize: 10 }}>&mdash;</span>
                            )}
                          </td>
                          <td style={s.td}>{p.state || "\u2014"}</td>
                          <td style={s.td}>
                            {onComparePatient && (
                              <button onClick={(e) => { e.stopPropagation(); onComparePatient(p); }}
                                style={{
                                  ...s.filterBtn,
                                  background: comparePatients.find(c => c.patient_id === p.patient_id) ? "rgba(124,58,237,0.15)" : "#131920",
                                  color: comparePatients.find(c => c.patient_id === p.patient_id) ? "#a78bfa" : "#4a6175",
                                  border: comparePatients.find(c => c.patient_id === p.patient_id) ? "1px solid rgba(124,58,237,0.3)" : "1px solid #1e2d3d",
                                }}>
                                {comparePatients.find(c => c.patient_id === p.patient_id) ? "Selected" : "Compare"}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          )}

          {pagination.pages > 1 && (
            <div style={s.pagRow}>
              <button onClick={() => search(query, page-1)} disabled={page<=1}
                style={{...s.pagBtn,opacity:page<=1?0.3:1}}>&larr; Prev</button>
              <span style={s.pagInfo}>Page {pagination.page} of {pagination.pages}</span>
              <button onClick={() => search(query, page+1)} disabled={page>=pagination.pages}
                style={{...s.pagBtn,opacity:page>=pagination.pages?0.3:1}}>Next &rarr;</button>
            </div>
          )}
        </>
      )}

      {!results && !error && !loading && (
        <div style={s.emptyState}><div style={{fontSize:24,marginBottom:8}}>&#x2315;</div><div>Search for patients to get started</div></div>
      )}
    </div>
  );
}

function fmtDate(d) { if(!d)return"\u2014"; try{return new Date(d).toLocaleDateString("en-US",{year:"numeric",month:"short",day:"numeric"});}catch{return d;} }

const s = {
  searchRow:{display:"flex",alignItems:"center",gap:10,background:"#131920",border:"1px solid #1e2d3d",borderRadius:10,padding:"0 14px",marginBottom:16},
  searchIcon:{fontSize:16,color:"#4a6175"},
  input:{flex:1,background:"none",border:"none",outline:"none",padding:"12px 0",color:"#e6edf3",fontSize:13,fontFamily:"inherit"},
  spinner:{width:16,height:16,border:"2px solid #1e2d3d",borderTopColor:"#00e5ff",borderRadius:"50%",animation:"spin 0.7s linear infinite"},
  errorBanner:{display:"flex",alignItems:"center",gap:8,background:"rgba(255,77,109,0.08)",border:"1px solid rgba(255,77,109,0.25)",borderRadius:8,padding:"10px 14px",marginBottom:12,fontSize:12,color:"#ff4d6d"},
  errorIcon:{width:18,height:18,borderRadius:"50%",background:"rgba(255,77,109,0.2)",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:11,flexShrink:0},
  retryBtn:{marginLeft:"auto",background:"rgba(255,77,109,0.15)",border:"1px solid rgba(255,77,109,0.3)",borderRadius:6,color:"#ff4d6d",padding:"4px 10px",fontSize:10,cursor:"pointer",fontFamily:"inherit"},
  resultsMeta:{fontSize:11,color:"#4a6175",marginBottom:10},
  table:{width:"100%",borderCollapse:"collapse"},
  th:{textAlign:"left",padding:"8px 10px",fontSize:9,fontWeight:600,color:"#4a6175",letterSpacing:1,textTransform:"uppercase",borderBottom:"1px solid #1e2d3d"},
  td:{padding:"10px 10px",fontSize:12,color:"#8b949e",borderBottom:"1px solid rgba(255,255,255,0.04)"},
  row:{cursor:"pointer",transition:"background 0.15s"},
  name:{color:"#e6edf3",fontWeight:600},
  mono:{fontFamily:"'JetBrains Mono',monospace",fontSize:10,color:"#4a6175"},
  badge:{padding:"2px 8px",borderRadius:20,fontSize:10,fontWeight:600,display:"inline-block"},
  emptyState:{textAlign:"center",color:"#4a6175",padding:"40px 0",fontSize:12},
  pagRow:{display:"flex",alignItems:"center",justifyContent:"center",gap:16,marginTop:16,paddingTop:12,borderTop:"1px solid #1e2d3d"},
  pagBtn:{background:"#131920",border:"1px solid #1e2d3d",borderRadius:6,color:"#8b949e",padding:"6px 14px",fontSize:11,cursor:"pointer",fontFamily:"inherit"},
  pagInfo:{fontSize:11,color:"#4a6175"},
  filterBtn:{borderRadius:6,padding:"5px 12px",fontSize:10,cursor:"pointer",fontWeight:600,fontFamily:"inherit",letterSpacing:0.5,transition:"all 0.15s"},
};
